// Client-side locale -> country -> currency inference.
//
// This is the "instant guess" half of the country/currency feature: it reads
// the device locale (web navigator, or Intl as a fallback) and derives an ISO
// 3166-1 alpha-2 country plus an ISO 4217 currency for display only. It never
// converts prices. Every environment access is guarded so React Native (where
// `navigator` and a full `Intl` may be undefined) never throws.

const DEFAULT_CURRENCY = 'USD';

// ISO 3166-1 alpha-2 country -> ISO 4217 currency.
//
// SOURCE OF TRUTH: this table mirrors the backend's authoritative map at
// `backend-nest/src/common/geo/country-currency.map.ts`. There is no shared
// package, so any entry added or changed there must be reflected here (and vice
// versa) to avoid the display currency diverging from the value the backend
// infers. Any country absent here resolves to the USD fallback.
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  // North America
  US: 'USD',
  CA: 'CAD',
  MX: 'MXN',
  // Latin America
  AR: 'ARS',
  BO: 'BOB',
  BR: 'BRL',
  CL: 'CLP',
  CO: 'COP',
  CR: 'CRC',
  CU: 'CUP',
  DO: 'DOP',
  EC: 'USD',
  GT: 'GTQ',
  HN: 'HNL',
  NI: 'NIO',
  PA: 'PAB',
  PE: 'PEN',
  PY: 'PYG',
  SV: 'USD',
  UY: 'UYU',
  VE: 'VES',
  // Eurozone members
  AT: 'EUR',
  BE: 'EUR',
  CY: 'EUR',
  DE: 'EUR',
  EE: 'EUR',
  ES: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  GR: 'EUR',
  HR: 'EUR',
  IE: 'EUR',
  IT: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  LV: 'EUR',
  MT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  SI: 'EUR',
  SK: 'EUR',
  // Rest of Europe
  GB: 'GBP',
  CH: 'CHF',
  NO: 'NOK',
  SE: 'SEK',
  DK: 'DKK',
  PL: 'PLN',
  CZ: 'CZK',
  HU: 'HUF',
  RO: 'RON',
  BG: 'BGN',
  UA: 'UAH',
  RU: 'RUB',
  TR: 'TRY',
  // Asia-Pacific
  JP: 'JPY',
  CN: 'CNY',
  IN: 'INR',
  ID: 'IDR',
  KR: 'KRW',
  SG: 'SGD',
  MY: 'MYR',
  TH: 'THB',
  PH: 'PHP',
  VN: 'VND',
  HK: 'HKD',
  TW: 'TWD',
  AU: 'AUD',
  NZ: 'NZD',
  // Middle East & Africa
  AE: 'AED',
  SA: 'SAR',
  IL: 'ILS',
  ZA: 'ZAR',
  EG: 'EGP',
  NG: 'NGN',
  KE: 'KES',
  MA: 'MAD',
};

export function currencyForCountry(country?: string | null): string {
  if (!country || typeof country !== 'string') {
    return DEFAULT_CURRENCY;
  }
  const code = country.trim().toUpperCase();
  return COUNTRY_TO_CURRENCY[code] ?? DEFAULT_CURRENCY;
}

export function normalizeCountryCode(country?: string | null): string | undefined {
  const normalized = country?.trim().toUpperCase();
  return normalized && Object.hasOwn(COUNTRY_TO_CURRENCY, normalized) ? normalized : undefined;
}

function parseRegionFromLocale(locale: string | null | undefined): string | null {
  if (!locale || typeof locale !== 'string') {
    return null;
  }
  const parts = locale.replace(/_/g, '-').split('-');
  // Skip the language subtag (index 0); the region subtag is a two-letter code.
  for (let i = 1; i < parts.length; i += 1) {
    const part = parts[i];
    if (/^[A-Za-z]{2}$/.test(part)) {
      return part.toUpperCase();
    }
  }
  return null;
}

function readNavigatorLocales(): string[] {
  const locales: string[] = [];
  try {
    if (typeof navigator !== 'undefined' && navigator) {
      const languages = (navigator as { languages?: unknown }).languages;
      if (Array.isArray(languages)) {
        for (const language of languages) {
          if (typeof language === 'string') {
            locales.push(language);
          }
        }
      }
      const language = (navigator as { language?: unknown }).language;
      if (typeof language === 'string') {
        locales.push(language);
      }
    }
  } catch {
    // Accessing navigator can throw on some platforms; ignore and continue.
  }
  return locales;
}

function readIntlLocale(): string | null {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
      const resolved = Intl.DateTimeFormat().resolvedOptions();
      if (resolved && typeof resolved.locale === 'string') {
        return resolved.locale;
      }
    }
  } catch {
    // Intl may be partially implemented on native; ignore and continue.
  }
  return null;
}

export function inferCountryFromLocale(): string | null {
  const candidates = [...readNavigatorLocales(), readIntlLocale()];
  for (const candidate of candidates) {
    const region = parseRegionFromLocale(candidate);
    if (region) {
      return region;
    }
  }
  return null;
}
